import { useState, useEffect, useRef } from "react";
import { DocumentRecord, EstadoDocumento } from "@/hooks/useDocuments";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Pencil, Trash2, Download, MapPin, Ship, User, Building2, DollarSign, Calendar, ChevronDown, X as XIcon, Package, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

const ESTADOS: EstadoDocumento[] = ["Pendiente", "Procesando", "Pagado", "Completado", "Cancelado"];

const ESTADO_STYLES: Record<EstadoDocumento, string> = {
  Pendiente: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/30",
  Procesando: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30",
  Pagado: "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30",
  Completado: "bg-primary/15 text-primary border-primary/30",
  Cancelado: "bg-destructive/15 text-destructive border-destructive/30",
};

interface Props {
  doc: DocumentRecord;
  isSelected: boolean;
  isFocused?: boolean;
  onSelect: (id: string | null) => void;
  onViewPdf: (url: string, title: string) => void;
  onEdit: (doc: DocumentRecord) => void;
  onDelete: (id: string) => void;
  onUpdateEstado: (id: string, estado: EstadoDocumento) => void;
  onUpdateDescuento?: (id: string, descuento: number) => void;
  showFinancial: boolean;
  showPacking: boolean;
  inventoryCount?: number;
}

function formatCreatedAt(dateStr: string): string {
  try {
    return format(new Date(dateStr), "dd/MM/yyyy HH:mm");
  } catch {
    return dateStr;
  }
}

function InfoRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-foreground truncate">{value}</span>
    </div>
  );
}

/** Locked metric display with flash effect */
function LockedMetric({ label, value, flash }: { label: string; value: string; flash: boolean }) {
  return (
    <div>
      <div className="flex items-center gap-1">
        <p className="text-[10px] text-muted-foreground">{label}</p>
        <Lock className="h-2.5 w-2.5 text-muted-foreground/50" />
      </div>
      <p className={cn(
        "text-sm font-bold font-mono text-foreground transition-colors duration-500",
        flash && "!text-primary animate-pulse",
      )}>
        {value}
      </p>
    </div>
  );
}

export function DocumentCardMobile({
  doc, isSelected, isFocused, onSelect, onViewPdf, onEdit, onDelete, onUpdateEstado, onUpdateDescuento, showFinancial, showPacking, inventoryCount = 0,
}: Props) {
  const estado = (doc.estado || "Pendiente") as EstadoDocumento;
  const [descuentoInput, setDescuentoInput] = useState(String(doc.descuento_ajuste ?? 0));
  const [flash, setFlash] = useState(false);
  const prevTotal = useRef(doc.total_factura);

  // Sync descuento input when doc changes externally
  useEffect(() => {
    setDescuentoInput(String(doc.descuento_ajuste ?? 0));
  }, [doc.descuento_ajuste]);

  // Flash when total changes
  useEffect(() => {
    if (doc.total_factura !== prevTotal.current) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 600);
      prevTotal.current = doc.total_factura;
      return () => clearTimeout(t);
    }
  }, [doc.total_factura]);

  const handleDescuentoBlur = () => {
    const parsed = parseFloat(descuentoInput) || 0;
    setDescuentoInput(String(parsed));
    if (onUpdateDescuento && parsed !== (doc.descuento_ajuste ?? 0)) {
      onUpdateDescuento(doc.id, parsed);
    }
  };

  return (
    <Card
      className={cn(
        "transition-all cursor-pointer active:scale-[0.99]",
        isSelected ? "border-primary ring-1 ring-primary/30" : "border-border"
      )}
      onClick={() => onSelect(isSelected ? null : doc.id)}
    >
      <CardContent className="p-4 space-y-3">
        {/* Focus mode close button */}
        {isFocused && (
          <div className="flex items-center justify-between -mt-1 -mx-1 mb-1">
            <span className="text-[11px] font-medium text-primary flex items-center gap-1">
              <Package className="h-3.5 w-3.5" />
              Mostrando {inventoryCount} ítem{inventoryCount !== 1 ? "s" : ""} de inventario
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); onSelect(null); }}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground bg-muted/50 rounded-full px-2 py-0.5"
            >
              <XIcon className="h-3 w-3" />
              Volver
            </button>
          </div>
        )}
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {doc.tipo_documento && (
              <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                {doc.tipo_documento}
              </span>
            )}
            <p className="text-sm font-semibold font-mono mt-1 truncate text-foreground">{doc.no_documento}</p>
          </div>
          <Select
            value={estado}
            onValueChange={(val) => { onUpdateEstado(doc.id, val as EstadoDocumento); }}
          >
            <SelectTrigger
              className="h-auto w-auto border-0 bg-transparent p-0 shadow-none focus:ring-0 [&>svg]:hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <Badge className={cn("text-[10px] cursor-pointer border whitespace-nowrap", ESTADO_STYLES[estado])}>
                {estado}
              </Badge>
            </SelectTrigger>
            <SelectContent>
              {ESTADOS.map((e) => (
                <SelectItem key={e} value={e}>
                  <span className="flex items-center gap-2">
                    <span className={cn("h-2 w-2 rounded-full", {
                      "bg-yellow-500": e === "Pendiente",
                      "bg-blue-500": e === "Procesando",
                      "bg-green-500": e === "Pagado",
                      "bg-primary": e === "Completado",
                      "bg-destructive": e === "Cancelado",
                    })} />
                    {e}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Body info */}
        <div className="space-y-1.5">
          <InfoRow icon={Calendar} label="Fecha" value={doc.fecha} />
          <InfoRow icon={Building2} label="Emisor" value={doc.emisor} />
          <InfoRow icon={User} label="Receptor" value={doc.receptor} />
          <InfoRow icon={MapPin} label="Origen" value={doc.origen} />
          <InfoRow icon={MapPin} label="Destino" value={doc.destino} />
          <InfoRow icon={Ship} label="Incoterm" value={doc.incoterm} />
        </div>

        {/* Financial highlight with descuento + locked totals */}
        {showFinancial && (
          <div className="space-y-2 bg-muted/50 rounded-lg px-3 py-2">
            {/* Total Factura — LOCKED */}
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-primary" />
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <p className="text-[10px] text-muted-foreground">Total Factura</p>
                  <Lock className="h-2.5 w-2.5 text-muted-foreground/50" />
                </div>
                <p className={cn(
                  "text-lg font-bold font-mono text-foreground transition-colors duration-500",
                  flash && "!text-primary animate-pulse",
                )}>
                  {doc.moneda || "$"} {Number(doc.total_factura ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              {Number(doc.costo_flete) > 0 && (
                <div className="text-right">
                  <p className="text-[10px] text-muted-foreground">Flete</p>
                  <p className="text-xs font-mono text-foreground">${Number(doc.costo_flete).toLocaleString()}</p>
                </div>
              )}
            </div>

            {/* Descuento / Ajuste — EDITABLE */}
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <div className="flex-1">
                <p className="text-[10px] text-muted-foreground mb-0.5">Descuento / Ajuste</p>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={descuentoInput}
                  onChange={(e) => setDescuentoInput(e.target.value)}
                  onBlur={handleDescuentoBlur}
                  className="h-7 text-xs font-mono"
                  placeholder="0"
                />
              </div>
              {Number(doc.impuestos) > 0 && (
                <div className="text-right">
                  <p className="text-[10px] text-muted-foreground">Impuestos</p>
                  <p className="text-xs font-mono text-foreground">${Number(doc.impuestos).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Packing highlight — LOCKED metrics */}
        {showPacking && (
          <div className="grid grid-cols-3 gap-2 bg-muted/50 rounded-lg px-3 py-2">
            <LockedMetric
              label="Piezas"
              value={Number(doc.total_piezas ?? doc.datos_extra?.total_piezas ?? 0).toLocaleString()}
              flash={flash}
            />
            <LockedMetric
              label="Peso (KG)"
              value={Number(doc.peso_bruto_kg ?? doc.datos_extra?.peso_bruto_kg ?? 0).toLocaleString()}
              flash={flash}
            />
            {((doc.volumen_cbm ?? doc.datos_extra?.volumen_cbm) != null) && (
              <LockedMetric
                label="Vol (CBM)"
                value={Number(doc.volumen_cbm ?? doc.datos_extra?.volumen_cbm).toLocaleString()}
                flash={false}
              />
            )}
          </div>
        )}

        {/* Logística complementaria (Neto / Volumen) — solo cuando hay dato y NO se mostró en packing */}
        {!showPacking && (doc.peso_neto_kg != null || doc.volumen_cbm != null) && (
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground border-t border-dashed border-border pt-2">
            {doc.peso_neto_kg != null && (
              <span className="font-mono tabular-nums">Neto: {Number(doc.peso_neto_kg).toLocaleString()} kg</span>
            )}
            {doc.volumen_cbm != null && (
              <span className="font-mono tabular-nums">Vol: {Number(doc.volumen_cbm).toLocaleString()} CBM</span>
            )}
          </div>
        )}

        {/* Metadata */}
        <p className="text-[10px] text-muted-foreground">Subido: {formatCreatedAt(doc.created_at)}</p>

        {/* Actions */}
        <div className="flex items-center gap-1 border-t border-border pt-3" onClick={(e) => e.stopPropagation()}>
          {doc.archivo_url && (
            <>
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-primary" onClick={() => onViewPdf(doc.archivo_url!, doc.no_documento)}>
                <Eye className="h-3.5 w-3.5" /> Ver / Descargar
              </Button>
            </>
          )}
          <div className="ml-auto flex gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => onEdit(doc)}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => onDelete(doc.id)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Inventory hint - only when NOT focused */}
        {!isFocused && inventoryCount > 0 && (
          <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground pt-1">
            <ChevronDown className="h-3.5 w-3.5" />
            <span>Ver {inventoryCount} ítem{inventoryCount !== 1 ? "s" : ""} de inventario</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
